<template>
    <v-container fill-height class="text-center">
        <v-row align-content="center" justify="center">
            <v-col cols="auto">
                <v-card flat color="transparent" class="text-center pt-6 pb-10" width="600">
                    <v-card-title class="justify-center opacity-7">{{ title }}</v-card-title>
                    <v-card-text class="opacity-7">
                        <v-icon size="120">{{ icon }}</v-icon>
                    </v-card-text>
                    <v-card-actions class="justify-center">
                        <div>
                            <v-btn outlined color="secondary" :href="documentationLink" target="_blank" class="ml-2">
                                <v-icon left>mdi-book-open-variant</v-icon>
                                documentation
                            </v-btn>
                            <v-btn v-if="isSpaceRested" outlined color="primary" @click="wakeSpace()" class="ml-2">
                                <v-icon left>mdi-bell-outline</v-icon>
                                Wake up
                            </v-btn>
                            <v-btn v-if="isSpaceArchived && isSpaceAdmin" outlined color="primary" @click="unarchiveDialog = true" class="ml-2">
                                <v-icon left>mdi-archive-arrow-up-outline</v-icon>
                                Unarchive
                            </v-btn>
                            <v-btn
                                v-if="isSpaceArchived"
                                outlined
                                color="primary"
                                :to="{
                                    name: 'instance-snapshots',
                                    params: { ...$route.params },
                                    query: $route.query
                                }"
                                class="ml-2">
                                <v-icon left>mdi-camera</v-icon>
                                Snapshots
                            </v-btn>
                        </div>
                    </v-card-actions>
                    <v-dialog v-model="unarchiveDialog" max-width="480" :persistent="unarchiving">
                        <v-card>
                            <v-card-title class="flex-nowrap align-start">
                                <span class="secondary--text">Unarchive space</span>
                                <v-spacer></v-spacer>
                                <v-btn :disabled="unarchiving" @click="unarchiveDialog = false" icon aria-label="Close"><v-icon>clear</v-icon></v-btn>
                            </v-card-title>
                            <v-card-text>
                                <p>Restoring the space takes a while. Pick the next archival date — the space re-archives once it passes.</p>
                                <v-menu
                                    ref="unarchiveDateMenu"
                                    v-model="unarchiveDateMenu"
                                    :close-on-content-click="false"
                                    :return-value.sync="archiveByDate"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="archiveByDate"
                                            label="Archive on"
                                            outlined
                                            dense
                                            readonly
                                            :disabled="unarchiving"
                                            v-bind="attrs"
                                            v-on="on"></v-text-field>
                                    </template>
                                    <nv-date-picker v-model="archiveByDate" :min="minArchiveByDate" no-title scrollable>
                                        <v-spacer></v-spacer>
                                        <v-btn text color="secondary" @click="unarchiveDateMenu = false">Cancel</v-btn>
                                        <v-btn text color="primary" @click="$refs.unarchiveDateMenu.save(archiveByDate)">OK</v-btn>
                                    </nv-date-picker>
                                </v-menu>
                                <v-alert v-if="unarchiveError" type="error" text>{{ unarchiveError }}</v-alert>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn color="primary" text :disabled="!archiveByDate || unarchiving" :loading="unarchiving" @click="unarchiveSpace()">
                                    Unarchive
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import NvDatePicker from '@/components/NvDatePicker.vue'
import { fetchTask } from '@/apis'
import { enumsData } from '@/mixins/enums'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(lifecycleErrors, permissionsErrors, snapshotErrors, spaceErrors)

export default {
    name: 'RestedCard',
    components: { NvDatePicker },
    mixins: [enumsData],
    props: {},
    data() {
        return {
            unarchiveDialog: false,
            unarchiveDateMenu: false,
            archiveByDate: '9999-12-31',
            minArchiveByDate: new Date().toISOString().slice(0, 10),
            unarchiving: false,
            unarchiveError: ''
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceArchived', 'isSpaceRested', 'isSpaceAdmin']),
        ...mapState('spaceStore', ['currentSpace']),
        title() {
            if (this.isSpaceArchived) {
                return `Space archived on ${this.$options.filters.dateTimeToHuman(this.currentSpace?.archival_timestamp)}`
            } else if (this.isSpaceRested) {
                return `Space resting since ${this.$options.filters.dateTimeToHuman(this.currentSpace?.rested_timestamp)}`
            } else {
                return ''
            }
        },
        icon() {
            if (this.isSpaceArchived) {
                return 'mdi-archive-outline'
            } else if (this.isSpaceRested) {
                return 'mdi-sleep'
            } else {
                return ''
            }
        },

        documentationLink() {
            if (this.isSpaceArchived) {
                return 'https://docs.nuvolos.com/how-to-guides/workflows-for-instructors/archiving-your-course'
            } else if (this.isSpaceRested) {
                return 'https://docs.nuvolos.com/administration/space-management/resting-spaces'
            } else {
                return ''
            }
        }
    },
    methods: {
        async wakeSpace() {
            try {
                const { data } = await this.$axios.post(`/spaces/${this.$route.params.sid}/wake`)
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                const taskResult = await fetchTask(data.tkid)
                if (taskResult instanceof Error) {
                    throw taskResult
                } else {
                    this.$store.dispatch('spaceStore/fetchCurrentSpace')
                }
            } catch (error) {
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                const snackBarText = getBackendErrorMessage(error, 'Failed to wake up space.')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
        },
        async unarchiveSpace() {
            this.unarchiving = true
            this.unarchiveError = ''
            try {
                const { data } = await this.$axios.put(`/spaces/${this.$route.params.sid}/unarchive`, {
                    archive_by_date: this.archiveByDate
                })
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                const taskResult = await fetchTask(data.tkid)
                if (taskResult instanceof Error) {
                    throw taskResult
                }
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                if (taskResult.data.status !== this.taskStatusTypes.COMPLETED.value) {
                    this.unarchiveError = 'Failed to unarchive space.'
                    return
                }
                this.unarchiveDialog = false
                this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
            } catch (error) {
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                this.unarchiveError = getBackendErrorMessage(error, 'Failed to unarchive space.') || ''
            } finally {
                this.unarchiving = false
            }
        }
    },
    mounted() {}
}
</script>

<style scoped></style>
